import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSgwtAuthentication } from '@sgwt/react-shared-libs';
import { useQueryParams } from 'use-query-params';
import { noop } from '../helpers';
import {
  ALL_QUERY_PARAM_KEYS,
  COMPLIANCE_QUERY_PARAM_KEYS,
  FILTER_QUERY_PARAM_KEYS,
  RECORD_QUERY_PARAM_KEYS,
  withQueryParams,
} from '../utils/withQueryParams';
import { computeQueryParameters } from './url/compute-url-param';
import { useGlobalState } from './GlobalStateContext';
import { useSession } from './SessionContext';

/**
 *  TYPES DECLARATION
 */
type UrlContextType = {
  resetFilters: () => void;
};

const UrlContext = createContext<UrlContextType>({
  resetFilters: noop,
});

UrlContext.displayName = 'UrlContext';
const QUERY_PARAM_KEYS = [...FILTER_QUERY_PARAM_KEYS, ...RECORD_QUERY_PARAM_KEYS, ...COMPLIANCE_QUERY_PARAM_KEYS];

const UrlProvider = withQueryParams<{ children: React.ReactNode }, typeof QUERY_PARAM_KEYS>(
  QUERY_PARAM_KEYS,
  ({ children, queryParams, setQueryParams }) => {
    const { filters, availableDates } = useGlobalState();
    const { userFull, profileType } = useSession();
    const { permissions } = useSgwtAuthentication();
    const [, _setQueryParams] = useQueryParams();
    const { search } = useLocation();

    /**
     * Remove unknown query params
     */
    useEffect(() => {
      const params = new URLSearchParams(search);
      const setQueryParams = Array.from(params.keys());
      const unknownQueryParams = setQueryParams.filter((key) => !ALL_QUERY_PARAM_KEYS.includes(key as any));
      const cleanedQueryParamsObject = unknownQueryParams.reduce((acc, curr) => {
        acc[curr] = undefined;
        return acc;
      }, {} as Record<string, undefined>);
      _setQueryParams(cleanedQueryParamsObject);
    }, []);

    const computeUrlParams = useCallback(() => {
      if (!filters || !availableDates || !userFull) return;

      const { computedFilterQueryParams } = computeQueryParameters(queryParams as any, filters, availableDates);

      const { _periodView, _order, _sort, ...queryParamsWithDefault } = queryParams as any;
      if (computedFilterQueryParams.order) {
        queryParamsWithDefault.order = 'ASC';
      }
      if (computedFilterQueryParams.sort) {
        queryParamsWithDefault.periodView = 'LAST_THREE_MONTHS';
      }
      if (computedFilterQueryParams.sort) {
        queryParamsWithDefault.sort = 'iAppliName';
      }

      if (
        Object.keys(computedFilterQueryParams).length !== Object.keys(queryParamsWithDefault).length ||
        Object.keys(computedFilterQueryParams).some(
          (key) => JSON.stringify(computedFilterQueryParams[key]) !== JSON.stringify(queryParamsWithDefault[key])
        )
      ) {
        setQueryParams(computedFilterQueryParams, 'replaceIn');
      }
    }, [queryParams, permissions, setQueryParams, availableDates, filters, userFull]);

    useEffect(() => {
      if (availableDates.length === 0 || filters.length === 0 || profileType === undefined) {
        return;
      }
      computeUrlParams();
    }, [queryParams, availableDates, filters, profileType, computeUrlParams]);

    const resetFilters = useCallback(() => {
      setQueryParams(
        {
          ...queryParams,
          query: undefined,
          applicationTypes: undefined,
          architectureTypes: undefined,
          componentStatus: undefined,
          countries: undefined,
          criticalities: undefined,
          functionalArchitecture: undefined,
          iam: undefined,
          infrastructureComponent: undefined,
          isAmerManaged: undefined,
          isCertified: undefined,
          isInternetExposed: undefined,
          languages: undefined,
          managedBy: undefined,
          managementRc: undefined,
          names: undefined,
          novaCompatibility: undefined,
          obsolescenceDate: undefined,
          ownedBy: undefined,
          ownershipRc: undefined,
          presenceResults: undefined,
          remediations: undefined,
          scopeResults: undefined,
          status: undefined,
          tags: undefined,
          tribes: undefined,
          trigrams: undefined,
          enablers: undefined,
          applicationRemediations: undefined,
          applicationRegulations: undefined,
        },
        'replaceIn'
      );
    }, [setQueryParams]);

    const value = useMemo(
      () => ({
        resetFilters,
      }),
      [resetFilters]
    );

    return <UrlContext.Provider value={value}>{children}</UrlContext.Provider>;
  }
);

export const useUrl = (): UrlContextType => {
  const context = useContext(UrlContext);
  if (context === undefined) {
    throw new Error(`useUrl must be used within an UrlProvider`);
  }
  return context;
};

export { UrlContext, UrlProvider };
